import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";

import css from './Pagination.module.css';
import { paginationActions } from "../../store/slices/pagination-slices";


const Pagination = ({ totalPages, totalItems }) => {
    const dispatch = useDispatch();
    const currentPageFromRedux = useSelector(state => state.pagination.page);
    const [currentPage, setCurrentPage] = useState(currentPageFromRedux);

    // Sync local state with Redux state
    useEffect(() => {
        setCurrentPage(currentPageFromRedux);
    }, [currentPageFromRedux]);

    // Handle edge cases when totalPages changes
    useEffect(() => {
        // If no results (totalPages = 0), reset to page 1
        if (totalPages === 0) {
            if (currentPageFromRedux !== 1) {
                dispatch(paginationActions.changePage(1));
            }
            return;
        }

        // If current page exceeds total pages, go to last page
        if (currentPageFromRedux > totalPages) {
            dispatch(paginationActions.changePage(totalPages));
        }
    }, [totalPages, currentPageFromRedux, dispatch]);

    const handlePageChange = (page) => {
        // Don't allow navigation if no pages
        if (totalPages === 0) return;
        
        if (page >= 1 && page <= totalPages) {
            dispatch(paginationActions.changePage(page));
        }
    };

    const handleInputChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 1 && value <= totalPages) {
            handlePageChange(value);
        }
    };

    // Don't render pagination if no data
    if (totalPages === 0) {
        return (
            <div className={css.paginationContainer}>
                <span className={css.totalItems}>Total: 0</span>
            </div>
        );
    }

    // Single page case
    if (totalPages === 1) {
        return (
            <div className={css.paginationContainer}>
                <button className={css.arrowButton} disabled>
                    &#60;
                </button>
                
                <button className={css.activePageButton}>1</button>
                
                <button className={css.arrowButton} disabled>
                    &#62;
                </button>

                <div className={css.goToContainer}>
                    <span>Go to</span>
                    <input
                        type="number"
                        value={1}
                        onChange={handleInputChange}
                        min="1"
                        max={1}
                        className={css.pageInput}
                    />
                </div>

                <span className={css.totalItems}>Total: {totalItems}</span>
            </div>
        );
    }

    return (
        <div className={css.paginationContainer}>
            <button
                className={css.arrowButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                &#60;
            </button>

            {/* First page */}
            <button
                className={currentPage === 1 ? css.activePageButton : css.pageButton}
                onClick={() => handlePageChange(1)}
            >
                1
            </button>

            {/* Dots before current page area */}
            {currentPage > 3 && <span className={css.dots}>...</span>}

            {/* Previous page */}
            {currentPage > 2 && (
                <button
                    className={css.pageButton}
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    {currentPage - 1}
                </button>
            )}

            {/* Current page (if not first or last) */}
            {currentPage !== 1 && currentPage !== totalPages && (
                <button className={css.activePageButton}>
                    {currentPage}
                </button>
            )}

            {/* Next page */}
            {currentPage < totalPages - 1 && (
                <button
                    className={css.pageButton}
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    {currentPage + 1}
                </button>
            )}

            {/* Dots after current page area */}
            {currentPage < totalPages - 2 && <span className={css.dots}>...</span>}

            {/* Last page (if more than 1 page) */}
            {totalPages > 1 && (
                <button
                    className={currentPage === totalPages ? css.activePageButton : css.pageButton}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </button>
            )}

            <button
                className={css.arrowButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                &#62;
            </button>

            <div className={css.goToContainer}>
                <span>Go to</span>
                <input
                    type="number"
                    value={currentPage}
                    onChange={handleInputChange}
                    min="1"
                    max={totalPages}
                    className={css.pageInput}
                />
            </div>

            <span className={css.totalItems}>Total: {totalItems}</span>
        </div>
    );
};

export { Pagination };
